import { Component, useRef, type ReactNode } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { ReloadNotice } from './ReloadNotice';

class ChunkBoundary extends Component<
  { children: ReactNode; reload: () => void; audience: boolean },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(error: unknown) {
    // Other application failures must remain visible to normal error reporting.
    if (
      !(error instanceof Error) ||
      ![
        'failed to fetch dynamically imported module',
        'importing a module script failed',
        'error loading dynamically imported module',
      ].some((message) => error.message.toLowerCase().includes(message))
    ) {
      throw error;
    }
    return { failed: true };
  }

  render() {
    return this.state.failed ? (
      <ReloadNotice
        reload={this.props.reload}
        automatic={this.props.audience}
        dismissible={false}
      />
    ) : (
      this.props.children
    );
  }
}

export const AppUpdate = ({
  children,
  audience = false,
}: {
  children: ReactNode;
  audience?: boolean;
}) => {
  const reloadRequested = useRef(false);
  const workerChanged = useRef(false);
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onNeedReload: () => {
      workerChanged.current = true;
      // Accepting an update in another tab must not interrupt this tab's call.
      if (reloadRequested.current) {
        window.location.reload();
      }
    },
  });
  const reload = () => {
    // The PWA helper activates the waiting worker and reloads the exact URL.
    // With no waiting worker, a stale import still needs a normal refresh.
    if (needRefresh && !workerChanged.current) {
      reloadRequested.current = true;
      void updateServiceWorker(true);
    } else {
      window.location.reload();
    }
  };
  return (
    <ChunkBoundary reload={reload} audience={audience}>
      {children}
      {needRefresh && !audience && <ReloadNotice reload={reload} />}
    </ChunkBoundary>
  );
};
